import { useEffect, useRef } from "react";

/**
 * Every successful change, announced; the views that show shared data re-read.
 *
 * Each page loads its own data, and every mutation used to have to know by hand
 * which OTHER views read what it changed. Three times one did not, and the only
 * alarm was a person reloading: the result count (2026-09), the document
 * reminder (2026-10-06), the submission count (2026-10-07). The callbacks stay —
 * `onProjectChanged`, `refreshDuties` — and this is the net under them, so the
 * next mutation nobody wires is still seen.
 *
 * `api.js` calls `announceChange` after every successful POST / PATCH / PUT /
 * DELETE and multipart upload — never after a GET, never under `/auth/`, and
 * never for an upload part: a transfer sends thousands, and nothing on screen
 * changes until `submit/finish`, which is a POST and announces.
 *
 * Kept out of api.js on purpose: 45 test files mock `../api` with a factory, and
 * a mocked api simply never announces rather than crashing on a missing export.
 */
const listeners = new Set();

export function announceChange(change) {
  listeners.forEach(fn => {
    // One broken view must not stop the others hearing about the change.
    try { fn(change); } catch { /* the listener's own problem */ }
  });
}

export function onDataChanged(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Re-read with `load` after any change made in this tab, and when the tab or
 * window comes back into view — the second is how a change somebody else made
 * (a quotation signed by another signatory) reaches a page left open.
 *
 * For a shared read-source only — the project a page shows, the counts in the
 * banner, a list. Not for a form: a re-read must never be able to replace what
 * someone is typing. `load` may change on every render; the latest is used. A
 * burst of announcements is one re-read, a refocus at most one per
 * `focusThrottleMs`, and a failed re-read keeps what is on screen.
 */
export function useLiveRefresh(load, { focusThrottleMs = 30000 } = {}) {
  const latest = useRef(load);
  latest.current = load;

  useEffect(() => {
    let timer = null;
    let lastFocusRead = -Infinity;
    const run = () => {
      try { Promise.resolve(latest.current?.()).catch(() => {}); } catch { /* keep what is shown */ }
    };
    const soon = () => {
      if (timer !== null) return;
      timer = setTimeout(() => { timer = null; run(); }, 0);
    };
    const onFocus = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastFocusRead < focusThrottleMs) return;
      lastFocusRead = now;
      soon();
    };

    const off = onDataChanged(soon);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      off();
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      if (timer !== null) clearTimeout(timer);
    };
  }, [focusThrottleMs]);
}
